
    import { computed, defineComponent, nextTick, onMounted, reactive } from 'vue'
    import router from '@/router'
    import { buildLocalization } from '@/util'
    import { useUserStateStore } from '@/store/userState'
    import { useMyEquipmentsStore } from '@/store/myEquipments'
    import { useBrowserStateStore } from '@/store/browserState'

    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import MyGrid from '@/components/grid/my-grid.vue'
    import Lazy from '@/components/common/lazy-render.vue'
    import { DateField } from '@/components/grid/fields'
    import EquipmentSerialNumberCell from '@/components/grid/cells/equipment-serial-number-cell.vue'

    import { IEquipmentMasterDataWithDocCountDTO, PartnerEquipmentRow } from '@/types/equipment'
    import SoftwareVersionCell from './cells/software-version-cell.vue'

    export default defineComponent({
        name: 'PartnerDevices',

        components: {
            Lazy,
            MyGrid,
            DateField,
            EquipmentSerialNumberCell,
            SoftwareVersionCell
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const myEquipmentsStore = useMyEquipmentsStore()
            const userStateStore = useUserStateStore()
            const browserState = useBrowserStateStore()
            const now = new Date()

            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            const state = reactive({
                renderGrid: false
            })

            const publicData = { state }

            /********************************************************************************************************************
             COMPUTED
             ********************************************************************************************************************/
            const gridState = userStateStore.myDevicesState
            const myEquipments = computed(() => myEquipmentsStore.devices)
            const equipmentRows = computed(() => myEquipments.value != null
                ? transform(myEquipments.value)
                : null
            )
            const noEquipments = computed(() => equipmentRows.value != null && equipmentRows.value.length == 0)

            const isAdmin = computed(() => {
                if (equipmentRows.value == null) return false
                return equipmentRows.value.map(row => row.soldTo)?.distinct().length > 1
            })

            const columns = computed(() => {
                let result: GridColumnProps[] = [
                    {
                        field: 'serialNo',
                        title: localize('serialNo'),
                        cell: 'custom1',
                        width: 150,
                        locked: true
                    }
                ]

                if (equipmentRows.value == null) return result

                // "SoldTo" only visible for admins
                if (isAdmin.value) {
                    result.push({
                        field: 'soldToInfo',
                        title: localize('soldTo'),
                        minResizableWidth: 100,
                        width: isNarrowScreen.value ? 200 : undefined
                    })
                }

                result.push(
                    {
                        field: 'description',
                        title: localize('description'),
                        minResizableWidth: 100,
                        width: isNarrowScreen.value ? 200 : undefined
                    },
                    {
                        field: 'salesOrderRef',
                        title: localize('salesOrderRef'),
                        minResizableWidth: 100,
                        width: isNarrowScreen.value ? 200 : undefined
                    },
                    {
                        field: 'contactPersonInfo',
                        title: localize('contactPerson'),
                        minResizableWidth: 100,
                        width: isNarrowScreen.value ? 200 : undefined
                    },
                    {
                        field: 'salesOrderNo',
                        title: localize('salesOrderNo'),
                        minResizableWidth: 100,
                        width: isNarrowScreen.value ? 100 : 200
                    },
                    {
                        field: 'deliveryDate',
                        title: localize('shippingDate'),
                        cell: 'custom2',
                        type: 'date',
                        minResizableWidth: 100,
                        width: 200
                    },
                    {
                        field: 'installedVersion',
                        title: localize('SoftwareVersion'),
                        cell: 'custom3',
                        minResizableWidth: 120,
                        width: 180
                    }
                )

                // "Software status" only visible for admins
                if (isAdmin.value) {
                    result.push({
                        field: 'softwareStatus',
                        title: localize('SoftwareStatus'),
                        minResizableWidth: 120,
                        width: 180
                    })
                }

                // Add column filters?
                if (isAdmin.value) {
                    for (var column of result) {
                        if (column.field != 'deliveryDate') 
                        column.columnMenu = 'checkboxFilter'
                    }
                }

                return result
            })

            /** Indicates whether the current browser width is narrow */
            const isNarrowScreen = computed(() => (browserState.resolution?.width ?? 1200) < 600)

            const computedData = { gridState, myEquipments, equipmentRows, noEquipments, columns, isAdmin, isNarrowScreen }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            function redirectToDevice(serialNo: string) {
                gridState.value.idOfCurrentRow = getId(serialNo)
                router.push(`/equipment/${serialNo}`)
            }

            function isFutureDate(date: Date): boolean {
                return date > now
            }

            function getId(serialNo: string | null): string {
                return serialNo
                    ? `SN_${serialNo}`
                    : ''
            }

            const publicMethods = { localize, redirectToDevice, isFutureDate, getId }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function transform(equipments: IEquipmentMasterDataWithDocCountDTO[]): PartnerEquipmentRow[] | null {
                const result = equipments.map(device => new PartnerEquipmentRow(device, localize))
                return result
            }

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(async () => {
                await nextTick()
                state.renderGrid = true
            })

            /********************************************************************************************************************
             INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
